import Vue from 'vue'
import router from '@/router'
import { MODULE_TYPES, isViewerRoute, formatRouteLabel, resolveNavigationLabel } from '@/constants/navigationTypes'
import { log, warn } from '@/utils/debugLogger'

/**
 * Navigation History Vuex Module
 *
 * Provides reactive state management for bidirectional navigation
 * between Viewer and Non-Viewer modules.
 *
 * Architecture:
 * - Uses flat reactive state instead of class instance (Vue 2 reactivity fix)
 * - Router afterEach hook records navigations
 * - ExpandableRail.vue consumes via getters
 *
 * Key concept: Records ALL navigations within facility context.
 * This enables full navigation chains like: Viewer → Register → TaskBoard → Register
 * Users can always navigate back to the previous page regardless of module type.
 *
 * Bug Fixes Applied:
 * - Phase 1: Flat reactive state (Vue 2 cannot detect class instance changes)
 * - Phase 2: Pre-seed history on facility load (first navigation now works)
 * - Phase 3: Two-phase navigation ID pattern (fixes race conditions)
 * - Phase 4: Record all navigations (not just boundary crossings) for full back chain
 * - Phase 5 (Issue #6): Prevent infinite history growth during GO_BACK
 *   - Moved programmatic nav check INTO RECORD_NAVIGATION action (avoids timing race)
 *   - Added duplicate consecutive entry check (same base path blocks recording)
 *   - Added "already at target" check in GO_BACK/GO_FORWARD (skip navigation if same path)
 * - Phase 6 (Issue #7): Fix router.replace() cascade triggering duplicate recording
 *   - The __navId URL cleanup via router.replace() triggers a SECOND afterEach
 *   - CLEAR_ACTIVE_NAVIGATION now happens in .then() callback AFTER replace completes
 *   - This keeps activeNavigationId set through BOTH afterEach hooks
 * - Phase 7 (Issue #12): Fix back button not displaying after non-viewer → viewer navigation
 *   - PROBLEM: Pre-seeding logic was blocked by early return when activeNavigationId was set
 *   - FIX 1: Move pre-seeding BEFORE the activeNavigationId early return check
 *   - FIX 2: Push source entry in GO_TO_VIEWER_SHORTCUT fallback before navigating
 *   - Now pre-seeding happens even during programmatic navigation (just recording is skipped)
 *
 * Debugging: Filter console by "[NavHistory]" to see all navigation logs
 */
export const NAVIGATION_HISTORY = {
  namespaced: true,

  // ─────────────────────────────────────────────────────────────
  // State - Flat reactive properties (Vue 2 compatible)
  // ─────────────────────────────────────────────────────────────
  state: () => ({
    // History entries (reactive array)
    entries: [],

    // Current position in history (browser-style pointer)
    currentIndex: -1,

    // Maximum history size
    maxSize: 50,

    // Unique navigation ID for two-phase pattern (replaces boolean isNavigating)
    // Set before programmatic navigation, cleared in afterEach hook
    activeNavigationId: null,

    // Current facility context - clear history on facility change
    facilityId: null
  }),

  getters: {
    // ─────────────────────────────────────────────────────────────
    // Navigation State
    // ─────────────────────────────────────────────────────────────

    /**
     * Can we navigate backward in history?
     */
    CAN_GO_BACK: state => state.currentIndex > 0,

    /**
     * Can we navigate forward in history?
     */
    CAN_GO_FORWARD: state => state.currentIndex < state.entries.length - 1,

    /**
     * Get the current history entry
     */
    CURRENT_ENTRY: state => {
      return state.currentIndex >= 0 ? state.entries[state.currentIndex] : null
    },

    /**
     * Get the entry we would navigate to if going back
     */
    BACK_ENTRY: state => {
      return state.currentIndex > 0 ? state.entries[state.currentIndex - 1] : null
    },

    /**
     * Get the entry we would navigate to if going forward
     */
    FORWARD_ENTRY: state => {
      return state.currentIndex < state.entries.length - 1
        ? state.entries[state.currentIndex + 1]
        : null
    },

    /**
     * Total number of entries in history
     */
    HISTORY_LENGTH: state => state.entries.length,

    /**
     * All history entries (for debugging)
     */
    ALL_ENTRIES: state => state.entries,

    // ─────────────────────────────────────────────────────────────
    // UI Labels (for buttons)
    // ─────────────────────────────────────────────────────────────

    /**
     * Human-readable label for back button
     * e.g., "Back to Tags Register"
     */
    BACK_LABEL: (state, getters) => {
      const entry = getters.BACK_ENTRY
      if (!entry) return null
      return entry.label || `Back to ${formatRouteLabel(entry.routeName)}`
    },

    /**
     * Human-readable label for forward button
     * e.g., "Go to Viewer"
     */
    FORWARD_LABEL: (state, getters) => {
      const entry = getters.FORWARD_ENTRY
      if (!entry) return null
      return entry.label || `Go to ${formatRouteLabel(entry.routeName)}`
    },

    // ─────────────────────────────────────────────────────────────
    // Context Checks
    // ─────────────────────────────────────────────────────────────

    /**
     * Check if back entry is a Viewer route
     * Used to show "Back to Viewer" button on non-viewer pages
     */
    BACK_IS_VIEWER: (state, getters) => {
      return getters.BACK_ENTRY?.moduleType === MODULE_TYPES.VIEWER
    },

    /**
     * Check if back entry is a Non-Viewer route
     * Used to show "Back to [Register/Dashboard]" button on viewer page
     */
    BACK_IS_NON_VIEWER: (state, getters) => {
      return getters.BACK_ENTRY?.moduleType === MODULE_TYPES.NON_VIEWER
    },

    // ─────────────────────────────────────────────────────────────
    // Viewer Shortcut Getters (Smart Navigation System)
    // ─────────────────────────────────────────────────────────────

    /**
     * Can we show the Viewer shortcut button?
     * Returns true when:
     * - User is NOT on a viewer page (we're on Register, Dashboard, etc.)
     * - There IS a viewer entry somewhere in history to jump to
     *
     * Note: The shortcut should be hidden when BACK already goes to Viewer
     * (that logic is handled in the component)
     *
     * @returns {boolean}
     */
    CAN_SHORTCUT_TO_VIEWER: state => {
      // Find any viewer entry in history
      return state.entries.some(entry => entry.moduleType === MODULE_TYPES.VIEWER)
    },

    /**
     * Get the viewer entry for shortcut navigation
     * Finds the MOST RECENT viewer entry in history (searching from end)
     * This allows jumping directly to the last known viewer location
     *
     * @returns {Object|null} The viewer history entry, or null if none exists
     */
    VIEWER_SHORTCUT_ENTRY: state => {
      // Search from the end to find the most recent viewer entry
      for (let i = state.entries.length - 1; i >= 0; i--) {
        if (state.entries[i].moduleType === MODULE_TYPES.VIEWER) {
          return state.entries[i]
        }
      }
      return null
    },

    // ─────────────────────────────────────────────────────────────
    // Context Menu Getters (Right-Click History)
    // ─────────────────────────────────────────────────────────────

    /**
     * Get last 5 entries before current position for context menu
     * Returns entries in reverse order (most recent first)
     * Each entry includes its actual index for direct navigation
     *
     * Used by ExpandableRail.vue for right-click history menu
     *
     * @returns {Array} Array of entries with actualIndex property
     */
    BACK_HISTORY_ENTRIES: state => {
      if (state.currentIndex <= 0) return []

      // Get entries before current position
      const backEntries = state.entries.slice(0, state.currentIndex)

      // Return last 5, reversed (most recent first), with actual indices
      return backEntries.slice(-5).reverse().map((entry, reverseIdx) => ({
        ...entry,
        actualIndex: state.currentIndex - 1 - reverseIdx  // Map back to real index
      }))
    }
  },

  mutations: {
    /**
     * Push a new entry to history (browser-style: truncates forward entries)
     */
    PUSH(state, entry) {
      // Truncate forward entries (browser behavior)
      if (state.currentIndex < state.entries.length - 1) {
        state.entries.splice(state.currentIndex + 1)
      }

      // Add entry with metadata - push() is reactive in Vue 2
      const newEntry = {
        key: `nav-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        timestamp: Date.now(),
        ...entry
      }
      state.entries.push(newEntry)
      state.currentIndex = state.entries.length - 1

      // Enforce max size
      while (state.entries.length > state.maxSize) {
        state.entries.shift()
        state.currentIndex = Math.max(0, state.currentIndex - 1)
      }
    },

    /**
     * Replace current entry without adding to history
     */
    REPLACE(state, entry) {
      if (state.currentIndex >= 0) {
        // Vue.set required for index-based assignment
        Vue.set(state.entries, state.currentIndex, {
          key: `nav-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          timestamp: Date.now(),
          ...entry
        })
      }
    },

    /**
     * Navigate backward (move pointer)
     */
    BACK(state) {
      if (state.currentIndex > 0) {
        state.currentIndex--
      }
    },

    /**
     * Navigate forward (move pointer)
     */
    FORWARD(state) {
      if (state.currentIndex < state.entries.length - 1) {
        state.currentIndex++
      }
    },

    /**
     * Clear all history
     */
    CLEAR(state) {
      state.entries = [] // Full array replacement is reactive
      state.currentIndex = -1
    },

    /**
     * Set active navigation ID (two-phase pattern)
     * Set BEFORE programmatic navigation starts
     */
    SET_ACTIVE_NAVIGATION(state, navId) {
      state.activeNavigationId = navId
    },

    /**
     * Clear active navigation ID (two-phase pattern)
     * Called in afterEach hook - only clears if ID matches
     */
    CLEAR_ACTIVE_NAVIGATION(state, navId) {
      // Only clear if the ID matches (prevents race conditions)
      if (state.activeNavigationId === navId) {
        state.activeNavigationId = null
      }
    },

    /**
     * Set current facility context
     */
    SET_FACILITY_ID(state, facilityId) {
      state.facilityId = facilityId
    },

    /**
     * Set current index directly (for context menu navigation)
     * Used by GO_TO_INDEX action to jump to specific history position
     */
    SET_CURRENT_INDEX(state, index) {
      state.currentIndex = index
    }
  },

  actions: {
    // ─────────────────────────────────────────────────────────────
    // Core Navigation Actions
    // ─────────────────────────────────────────────────────────────

    /**
     * Initialize history with the current location (pre-seed pattern)
     * Fixes: First navigation now shows back button immediately
     *
     * @param {Object} payload - { route, facilityId }
     */
    INITIALIZE_HISTORY({ commit, state, rootGetters }, { route, facilityId }) {
      // Only initialize if empty and we have a facility context
      if (state.entries.length === 0 && facilityId) {
        const entry = {
          path: route.fullPath,
          routeName: route.name,
          params: { ...route.params },
          query: { ...route.query },
          moduleType: isViewerRoute(route.name) ? MODULE_TYPES.VIEWER : MODULE_TYPES.NON_VIEWER,
          label: resolveNavigationLabel(route.name, route.params, rootGetters) || route.meta?.title || formatRouteLabel(route.name),
          facilityId,
          isInitialEntry: true
        }

        log('[NavHistory]', 'INITIALIZE_HISTORY: pre-seeding with initial entry', {
          routeName: route.name,
          moduleType: entry.moduleType,
          facilityId
        })

        commit('PUSH', entry)
        commit('SET_FACILITY_ID', facilityId)
      }
    },

    /**
     * Record a navigation event (called from router afterEach)
     *
     * Records ALL navigations within facility context to enable full navigation chains.
     * Examples:
     * - Viewer → Register: "Back to Viewer"
     * - Register → TaskBoard: "Back to Register"
     * - TaskBoard → Register: "Back to TaskBoard"
     *
     * Only skips duplicate navigations (same route) to prevent history clutter.
     *
     * @param {Object} payload - { from, to } route objects
     */
    RECORD_NAVIGATION({ commit, state, dispatch, rootGetters }, { from, to }) {
      log('[NavHistory]', 'RECORD_NAVIGATION called', {
        from: from.name,
        to: to.name,
        fromPath: from.fullPath,
        toPath: to.fullPath,
        activeNavigationId: state.activeNavigationId,
        facilityId: state.facilityId,
        historyLength: state.entries.length,
        currentIndex: state.currentIndex
      })

      // Extract facility IDs FIRST (needed for pre-seeding)
      const toFacilityId = to.params?.id
      const fromFacilityId = from.params?.id

      // ═══════════════════════════════════════════════════════════════════
      // CRITICAL FIX (Issue #12): Pre-seed BEFORE activeNavigationId check
      //
      // When navigating from non-viewer to viewer via action button, the
      // activeNavigationId is set BEFORE navigation. This caused pre-seeding
      // to be skipped, resulting in no "Back to Register" button.
      //
      // Pre-seed when history is empty, even during programmatic navigation.
      // ═══════════════════════════════════════════════════════════════════
      if (state.entries.length === 0 && toFacilityId && from.name) {
        log('[NavHistory]', 'PRE-SEEDING: history empty during navigation', {
          from: from.name,
          to: to.name,
          isProgrammatic: !!state.activeNavigationId
        })
        dispatch('INITIALIZE_HISTORY', { route: from, facilityId: fromFacilityId || toFacilityId })
      }

      // Skip recording if programmatic navigation is active (two-phase pattern)
      // NOTE: Pre-seeding above still happens - this only skips RECORDING destination
      if (state.activeNavigationId) {
        log('[NavHistory]', 'SKIPPED: programmatic navigation in progress', {
          activeNavigationId: state.activeNavigationId,
          preSeeded: state.entries.length > 0
        })
        return
      }

      // Skip if no valid from route (initial page load)
      if (!from.name) {
        log('[NavHistory]', 'SKIPPED: no from.name (initial page load)')
        return
      }

      // Clear history on facility change
      // Use state.facilityId instead of fromFacilityId for reliable detection
      // This handles cases like coming from Dashboard (no facility) to a facility page
      if (toFacilityId && state.facilityId && toFacilityId !== state.facilityId) {
        log('[NavHistory]', 'FACILITY_CHANGED: clearing history', {
          oldFacility: state.facilityId,
          newFacility: toFacilityId,
          clearedEntries: state.entries.length
        })
        commit('CLEAR')
        commit('SET_FACILITY_ID', toFacilityId)
        // Don't return - continue to record the new facility entry below
      }

      // Only track within facility context
      if (!toFacilityId) {
        log('[NavHistory]', 'SKIPPED: no facility context (toFacilityId is null)')
        return
      }

      // Update facility context
      commit('SET_FACILITY_ID', toFacilityId)

      // Determine module types for both routes
      const fromIsViewer = isViewerRoute(from.name)
      const toIsViewer = isViewerRoute(to.name)

      log('[NavHistory]', 'BOUNDARY_CHECK', {
        fromRoute: from.name,
        toRoute: to.name,
        fromIsViewer,
        toIsViewer,
        crossingBoundary: fromIsViewer !== toIsViewer
      })

      // Record ALL navigation within facility context
      // This enables full navigation chain: Viewer → Register → TaskBoard → Register

      // Skip only if navigating to the SAME route (prevents duplicates)
      if (from.fullPath === to.fullPath) {
        log('[NavHistory]', 'SKIPPED: same route (duplicate)', { path: from.fullPath })
        return
      }

      // Prevent duplicate consecutive entries (same base path without query)
      // This fixes the "infinite history growth" bug during GO_BACK
      // where navigation to the same path was being recorded repeatedly
      const toBasePath = to.fullPath.split('?')[0]
      const lastEntry = state.entries[state.currentIndex]
      if (lastEntry) {
        const lastBasePath = lastEntry.path.split('?')[0]
        if (lastBasePath === toBasePath) {
          log('[NavHistory]', 'SKIPPED: duplicate consecutive entry', {
            path: toBasePath,
            lastPath: lastBasePath
          })
          return
        }
      }

      // Log for debugging
      log('[NavHistory]', 'RECORDING', {
        from: from.name,
        to: to.name,
        isBoundaryCrossing: fromIsViewer !== toIsViewer,
        recordingDestination: to.name
      })

      // Create history entry for the route we're ARRIVING AT (destination)
      // This ensures the current page is always in history, fixing the "delayed back button" bug
      // Before: recorded source → back button appeared one navigation late
      // After: record destination → back button appears immediately
      const entry = {
        path: to.fullPath,
        routeName: to.name,
        params: { ...to.params },
        query: { ...to.query },
        moduleType: toIsViewer ? MODULE_TYPES.VIEWER : MODULE_TYPES.NON_VIEWER,
        label: resolveNavigationLabel(to.name, to.params, rootGetters) || to.meta?.title || formatRouteLabel(to.name),
        facilityId: toFacilityId
      }

      log('[NavHistory]', 'PUSH_ENTRY', {
        entry,
        historyLengthBefore: state.entries.length,
        currentIndex: state.currentIndex
      })

      commit('PUSH', entry)

      log('[NavHistory]', 'ENTRY_PUSHED', {
        historyLengthAfter: state.entries.length,
        newIndex: state.currentIndex,
        canGoBack: state.currentIndex > 0,
        canGoForward: state.currentIndex < state.entries.length - 1
      })

      // Issue #10 Debug: Log full state after push for comprehensive debugging
      log('[NavHistory]', 'STATE_AFTER_PUSH', {
        entries: state.entries.map(e => ({
          name: e.routeName,
          type: e.moduleType,
          path: e.path.split('?')[0] // Clean path without query
        })),
        currentIndex: state.currentIndex,
        canGoBack: state.currentIndex > 0,
        canGoForward: state.currentIndex < state.entries.length - 1,
        currentEntry: state.entries[state.currentIndex]?.routeName || null,
        backEntry: state.currentIndex > 0 ? state.entries[state.currentIndex - 1]?.routeName : null
      })
    },

    /**
     * Navigate back in history
     * Uses two-phase navigation ID pattern to prevent race conditions
     *
     * @returns {Promise<boolean>} True if navigation succeeded
     */
    async GO_BACK({ commit, state, dispatch, getters }) {
      const canGoBack = getters.CAN_GO_BACK

      log('[NavHistory]', 'GO_BACK called', {
        canGoBack,
        currentIndex: state.currentIndex,
        historyLength: state.entries.length,
        backEntry: getters.BACK_ENTRY
      })

      if (!canGoBack) {
        warn('[NavHistory]', 'GO_BACK failed: cannot go back (canGoBack is false)')
        return false
      }

      const entry = getters.BACK_ENTRY
      if (!entry) {
        warn('[NavHistory]', 'GO_BACK failed: no back entry available')
        return false
      }

      // Skip navigation if already at target (prevents NavigationDuplicated errors)
      // This can happen if duplicate entries exist in history
      const currentBasePath = router.currentRoute.fullPath.split('?')[0]
      const targetBasePath = entry.path.split('?')[0]
      if (currentBasePath === targetBasePath) {
        log('[NavHistory]', 'GO_BACK: already at target, moving pointer only', {
          currentPath: currentBasePath,
          targetPath: targetBasePath
        })
        // Move pointer without navigation
        commit('BACK')
        return true
      }

      log('[NavHistory]', 'GO_BACK executing', {
        targetPath: entry.path,
        targetRoute: entry.routeName,
        moduleType: entry.moduleType,
        label: entry.label
      })

      // Generate unique navigation ID (two-phase pattern)
      const navId = `nav-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

      // Set BEFORE navigation starts
      commit('SET_ACTIVE_NAVIGATION', navId)
      log('[NavHistory]', 'SET_ACTIVE_NAVIGATION:', navId)

      try {
        // Move the history pointer
        commit('BACK')
        log('[NavHistory]', 'BACK mutation committed', {
          newIndex: state.currentIndex,
          newCurrent: getters.CURRENT_ENTRY?.routeName
        })

        // Perform the actual navigation with navId in query for afterEach
        await router.push({
          path: entry.path,
          query: { ...entry.query, __navId: navId }
        })
        log('[NavHistory]', 'Router push completed', { path: entry.path })

        // Collapse rail after navigation for cleaner UX
        dispatch('GENERAL/COLLAPSE_RAIL', null, { root: true })

        return true
      } catch (err) {
        // Clear on error (navigation failed)
        commit('CLEAR_ACTIVE_NAVIGATION', navId)

        // Navigation might fail if same route - that's OK
        if (err.name !== 'NavigationDuplicated') {
          warn('[NavHistory]', 'GO_BACK error', { error: err.message, name: err.name })
        } else {
          log('[NavHistory]', 'Navigation duplicated (same route) - OK')
        }
        return false
      }
      // NO finally block - afterEach clears the flag
    },

    /**
     * Navigate forward in history
     * Uses two-phase navigation ID pattern to prevent race conditions
     *
     * @returns {Promise<boolean>} True if navigation succeeded
     */
    async GO_FORWARD({ commit, state, dispatch, getters }) {
      const canGoForward = getters.CAN_GO_FORWARD

      log('[NavHistory]', 'GO_FORWARD called', {
        canGoForward,
        currentIndex: state.currentIndex,
        historyLength: state.entries.length,
        forwardEntry: getters.FORWARD_ENTRY
      })

      if (!canGoForward) {
        warn('[NavHistory]', 'GO_FORWARD failed: cannot go forward (canGoForward is false)')
        return false
      }

      const entry = getters.FORWARD_ENTRY
      if (!entry) {
        warn('[NavHistory]', 'GO_FORWARD failed: no forward entry available')
        return false
      }

      // Skip navigation if already at target (prevents NavigationDuplicated errors)
      const currentBasePath = router.currentRoute.fullPath.split('?')[0]
      const targetBasePath = entry.path.split('?')[0]
      if (currentBasePath === targetBasePath) {
        log('[NavHistory]', 'GO_FORWARD: already at target, moving pointer only', {
          currentPath: currentBasePath,
          targetPath: targetBasePath
        })
        commit('FORWARD')
        return true
      }

      log('[NavHistory]', 'GO_FORWARD executing', {
        targetPath: entry.path,
        targetRoute: entry.routeName,
        moduleType: entry.moduleType,
        label: entry.label
      })

      // Generate unique navigation ID (two-phase pattern)
      const navId = `nav-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

      // Set BEFORE navigation starts
      commit('SET_ACTIVE_NAVIGATION', navId)
      log('[NavHistory]', 'SET_ACTIVE_NAVIGATION:', navId)

      try {
        // Move the history pointer
        commit('FORWARD')
        log('[NavHistory]', 'FORWARD mutation committed', {
          newIndex: state.currentIndex,
          newCurrent: getters.CURRENT_ENTRY?.routeName
        })

        // Perform the actual navigation with navId in query for afterEach
        await router.push({
          path: entry.path,
          query: { ...entry.query, __navId: navId }
        })
        log('[NavHistory]', 'Router push completed', { path: entry.path })

        // Collapse rail after navigation for cleaner UX
        dispatch('GENERAL/COLLAPSE_RAIL', null, { root: true })

        return true
      } catch (err) {
        // Clear on error (navigation failed)
        commit('CLEAR_ACTIVE_NAVIGATION', navId)

        if (err.name !== 'NavigationDuplicated') {
          warn('[NavHistory]', 'GO_FORWARD error', { error: err.message, name: err.name })
        } else {
          log('[NavHistory]', 'Navigation duplicated (same route) - OK')
        }
        return false
      }
      // NO finally block - afterEach clears the flag
    },

    /**
     * Navigate to a specific history index (for context menu)
     * Uses two-phase navigation ID pattern to prevent race conditions
     *
     * Unlike GO_BACK which moves one step, this jumps directly to any
     * previous position in history. Used by right-click context menu.
     *
     * @param {number} targetIndex - The index in entries array to navigate to
     * @returns {Promise<boolean>} True if navigation succeeded
     */
    async GO_TO_INDEX({ commit, state, dispatch }, targetIndex) {
      log('[NavHistory]', 'GO_TO_INDEX called', {
        targetIndex,
        currentIndex: state.currentIndex,
        historyLength: state.entries.length
      })

      // Validate index - must be less than current (going back)
      if (targetIndex < 0 || targetIndex >= state.currentIndex) {
        warn('[NavHistory]', 'GO_TO_INDEX: invalid target index', {
          targetIndex,
          currentIndex: state.currentIndex,
          reason: targetIndex < 0 ? 'negative index' : 'not a back navigation'
        })
        return false
      }

      const entry = state.entries[targetIndex]
      if (!entry) {
        warn('[NavHistory]', 'GO_TO_INDEX: no entry at index', { targetIndex })
        return false
      }

      // Skip if already at target (prevents NavigationDuplicated errors)
      const currentBasePath = router.currentRoute.fullPath.split('?')[0]
      const targetBasePath = entry.path.split('?')[0]
      if (currentBasePath === targetBasePath) {
        log('[NavHistory]', 'GO_TO_INDEX: already at target, moving pointer only', {
          currentPath: currentBasePath,
          targetPath: targetBasePath
        })
        commit('SET_CURRENT_INDEX', targetIndex)
        return true
      }

      log('[NavHistory]', 'GO_TO_INDEX executing', {
        targetPath: entry.path,
        targetRoute: entry.routeName,
        moduleType: entry.moduleType,
        label: entry.label,
        jumpingSteps: state.currentIndex - targetIndex
      })

      // Generate unique navigation ID (two-phase pattern - CRITICAL)
      const navId = `nav-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

      // Set BEFORE navigation starts
      commit('SET_ACTIVE_NAVIGATION', navId)
      log('[NavHistory]', 'SET_ACTIVE_NAVIGATION:', navId)

      try {
        // Update index directly to target (jump, not step)
        commit('SET_CURRENT_INDEX', targetIndex)
        log('[NavHistory]', 'SET_CURRENT_INDEX committed', {
          newIndex: state.currentIndex
        })

        // Perform the actual navigation with navId to prevent re-recording
        await router.push({
          path: entry.path,
          query: { ...entry.query, __navId: navId }
        })
        log('[NavHistory]', 'GO_TO_INDEX: Router push completed', { path: entry.path })

        // Collapse rail after navigation for cleaner UX
        dispatch('GENERAL/COLLAPSE_RAIL', null, { root: true })

        return true
      } catch (err) {
        // Clear on error (navigation failed)
        commit('CLEAR_ACTIVE_NAVIGATION', navId)

        if (err.name !== 'NavigationDuplicated') {
          warn('[NavHistory]', 'GO_TO_INDEX error', { error: err.message, name: err.name })
        } else {
          log('[NavHistory]', 'GO_TO_INDEX: Navigation duplicated (same route) - OK')
        }
        return false
      }
      // NO finally block - afterEach clears the flag
    },

    /**
     * Clear history (e.g., on logout or facility change)
     */
    CLEAR_HISTORY({ commit, state }) {
      log('[NavHistory]', 'CLEAR_HISTORY called', {
        entriesBeforeClear: state.entries.length
      })
      commit('CLEAR')
      log('[NavHistory]', 'History cleared')
    },

    // ─────────────────────────────────────────────────────────────
    // Smart Navigation Actions (Viewer Shortcut)
    // ─────────────────────────────────────────────────────────────

    /**
     * Shortcut to jump directly to Viewer (home base)
     *
     * Unlike GO_BACK, this doesn't follow the history chain.
     * It jumps directly to the last known Viewer location.
     *
     * Key behavior:
     * 1. Pushes current location to history FIRST (so user can "Back to [current]" after)
     * 2. Then navigates to the Viewer entry
     *
     * This preserves the navigation chain for the user's return journey.
     *
     * @returns {Promise<boolean>} True if navigation succeeded
     */
    async GO_TO_VIEWER_SHORTCUT({ commit, state, getters, dispatch, rootGetters }) {
      const viewerEntry = getters.VIEWER_SHORTCUT_ENTRY

      log('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT called', {
        hasViewerEntry: !!viewerEntry,
        viewerPath: viewerEntry?.path,
        currentIndex: state.currentIndex,
        historyLength: state.entries.length
      })

      // Issue #9 fix: Fallback to facility route if no viewer entry in history
      // This handles direct URL navigation to non-viewer routes (e.g., /register)
      if (!viewerEntry) {
        log('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT: no viewer entry, using facility fallback')

        // Get current facility ID from route or state
        const currentFacilityId = router.currentRoute.params?.id || state.facilityId

        if (!currentFacilityId) {
          warn('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT failed: no facility context')
          return false
        }

        // Navigate to facility route directly
        const navId = `nav-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
        commit('SET_ACTIVE_NAVIGATION', navId)

        try {
          // ═══════════════════════════════════════════════════════════════════
          // FIX (Issue #12): Push source entry BEFORE navigating to facility
          // This ensures "Back to [Source]" button works after arrival
          // ═══════════════════════════════════════════════════════════════════
          const currentRoute = router.currentRoute
          const sourceEntry = {
            path: currentRoute.fullPath,
            routeName: currentRoute.name,
            params: { ...currentRoute.params },
            query: { ...currentRoute.query },
            moduleType: isViewerRoute(currentRoute.name) ? MODULE_TYPES.VIEWER : MODULE_TYPES.NON_VIEWER,
            label: resolveNavigationLabel(currentRoute.name, currentRoute.params, rootGetters) || currentRoute.meta?.title || formatRouteLabel(currentRoute.name),
            facilityId: currentRoute.params?.id
          }

          // Only push if different from last entry (avoid duplicates)
          const lastEntry = state.entries[state.currentIndex]
          const isDifferent = !lastEntry || lastEntry.routeName !== sourceEntry.routeName ||
            lastEntry.path.split('?')[0] !== sourceEntry.path.split('?')[0]
          if (isDifferent) {
            commit('PUSH', sourceEntry)
            log('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT fallback: pushed source entry', {
              sourceEntry: sourceEntry.routeName,
              historyLength: state.entries.length
            })
          }

          await router.push({
            name: 'facility',
            params: { id: currentFacilityId },
            query: { __navId: navId }
          })

          // Push facility entry to history (since afterEach skips programmatic navigation)
          const facilityEntry = {
            path: `/${currentFacilityId}`,
            routeName: 'facility',
            params: { id: currentFacilityId },
            query: {},
            moduleType: MODULE_TYPES.VIEWER,
            label: 'Facility',
            facilityId: currentFacilityId
          }
          commit('PUSH', facilityEntry)

          log('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT: navigated to facility fallback', {
            facilityId: currentFacilityId,
            historyUpdated: true,
            newLength: state.entries.length,
            newIndex: state.currentIndex,
            canGoBack: state.currentIndex > 0
          })
          dispatch('GENERAL/COLLAPSE_RAIL', null, { root: true })
          return true
        } catch (err) {
          commit('CLEAR_ACTIVE_NAVIGATION', navId)
          if (err.name !== 'NavigationDuplicated') {
            warn('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT fallback error', { error: err.message })
          } else {
            log('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT fallback: Navigation duplicated (same route) - OK')
          }
          return false
        }
      }

      // Generate unique navigation ID (two-phase pattern)
      const navId = `nav-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

      // Set BEFORE navigation starts
      commit('SET_ACTIVE_NAVIGATION', navId)
      log('[NavHistory]', 'SET_ACTIVE_NAVIGATION:', navId)

      try {
        // Step 1: Check if current location is already the last entry in history
        // This prevents duplicate entries when using the shortcut
        const currentRoute = router.currentRoute
        const lastEntry = state.entries[state.currentIndex]

        const currentEntry = {
          path: currentRoute.fullPath,
          routeName: currentRoute.name,
          params: { ...currentRoute.params },
          query: { ...currentRoute.query },
          moduleType: isViewerRoute(currentRoute.name) ? MODULE_TYPES.VIEWER : MODULE_TYPES.NON_VIEWER,
          label: resolveNavigationLabel(currentRoute.name, currentRoute.params, rootGetters) || currentRoute.meta?.title || formatRouteLabel(currentRoute.name),
          facilityId: currentRoute.params?.id
        }

        // Only push if current location differs from last entry (avoid duplicates)
        const isDifferentFromLast = !lastEntry ||
          lastEntry.routeName !== currentEntry.routeName ||
          lastEntry.path.split('?')[0] !== currentEntry.path.split('?')[0]

        if (isDifferentFromLast) {
          log('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT: pushing current location', {
            currentEntry,
            beforePush: { entries: state.entries.length, index: state.currentIndex }
          })

          commit('PUSH', currentEntry)

          log('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT: current location pushed', {
            afterPush: { entries: state.entries.length, index: state.currentIndex }
          })
        } else {
          log('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT: current location already in history, skipping push to avoid duplicate')
        }

        // Step 2: Navigate to the viewer entry
        log('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT: navigating to viewer', {
          targetPath: viewerEntry.path,
          targetRoute: viewerEntry.routeName
        })

        await router.push({
          path: viewerEntry.path,
          query: { ...viewerEntry.query, __navId: navId }
        })

        // Step 3: Push the viewer entry to history
        // Since afterEach skips programmatic navigation, we need to manually record the destination
        const viewerEntryFull = {
          path: viewerEntry.path,
          routeName: viewerEntry.routeName,
          params: { ...viewerEntry.params },
          query: { ...viewerEntry.query },
          moduleType: MODULE_TYPES.VIEWER,
          label: viewerEntry.label || 'Facility',
          facilityId: viewerEntry.facilityId
        }

        commit('PUSH', viewerEntryFull)

        log('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT: navigation complete', {
          viewerEntryPushed: viewerEntryFull.routeName,
          finalState: { entries: state.entries.length, index: state.currentIndex }
        })

        // Collapse rail after navigation for cleaner UX
        dispatch('GENERAL/COLLAPSE_RAIL', null, { root: true })

        return true
      } catch (err) {
        // Clear on error (navigation failed)
        commit('CLEAR_ACTIVE_NAVIGATION', navId)

        // Navigation might fail if same route - that's OK
        if (err.name !== 'NavigationDuplicated') {
          warn('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT error', { error: err.message, name: err.name })
        } else {
          log('[NavHistory]', 'GO_TO_VIEWER_SHORTCUT: Navigation duplicated (same route) - OK')
        }
        return false
      }
      // NO finally block - afterEach clears the flag
    }
  }
}

export default NAVIGATION_HISTORY
